import logger from '../assets/js/logger';
import {Activity, Contact, Deal, Lead, User} from '../model';
import keyBy from 'lodash/keyBy'
import map from 'lodash/map'
import sortBy from 'lodash/sortBy'
import flatten from 'lodash/flatten'

const getActivities = filter => {
   logger.debug(filter);
   let list = [];

   const bxFilter = { };
   if (filter.type && filter.type.length > 0) {
      bxFilter['=TYPE_ID'] = flatten(filter.type)
   }
   if (filter.user && filter.user.length > 0) {
      bxFilter['=RESPONSIBLE_ID'] = filter.user
   }

   setDatesFilter(bxFilter, 'START_TIME', filter.dates);

   if (Object.keys(bxFilter).length === 0) return [];

   return new Promise((resolve, reject) => {
      BX24.callMethod('crm.activity.list', { filter: bxFilter, order: { 'START_TIME': 'ASC' } }, response => {
         if (response.error()) {
            reject(response.error());
         } else {
            if (Array.isArray(response.data())) {
               list = list.concat(response.data().map(item => new Activity(item)))
            }

            if (response.more()) {
               response.next();
            } else {
               resolve(list);
            }
         }
      })
   })
};

const getDeals = filter => {
   logger.debug(filter);
   let list = [];

   const bxFilter = { };

   if (filter.idSet && filter.idSet.size > 0) {
      bxFilter['ID'] = Array.from(filter.idSet)
   }

   if (filter.type && filter.type.length > 0) {
      bxFilter['=TYPE_ID'] = filter.type
   }

   setDatesFilter(bxFilter, 'BEGINDATE', filter.datesDeal);

   if (Object.keys(bxFilter).length === 0) return [];

   return new Promise((resolve, reject) => {
      BX24.callMethod('crm.deal.list', { filter: bxFilter, order: { 'BEGINDATE': 'ASC' } }, response => {
         if (response.error()) {
            reject(response.error());
         } else {
            if (Array.isArray(response.data())) {
               list = list.concat(response.data().map(item => new Deal(item)))
            }

            if (response.more()) {
               response.next();
            } else {
               resolve(list);
            }
         }
      })
   })
};

const getContactList = idSet => {
   if (idSet.size === 0) return [];

   let list = [];
   return new Promise((resolve, reject) => {
      BX24.callMethod( "crm.contact.list", {
            filter: { ID: Array.from(idSet) },
            select: [ "*", "UF_*", "PHONE", "EMAIL" ]
         },
         response => {
            if (response.error()) {
               reject(response.error());
            } else {
               if (Array.isArray(response.data())) {
                  list = list.concat(response.data().map(item => new Contact(item)))
               }

               if (response.more()) {
                  response.next();
               } else {
                  resolve(list);
               }
            }
         }
      );
   })
};

const getLeadList = idSet => {
   if (idSet.size === 0) return [];

   let list = [];
   return new Promise((resolve, reject) => {
      BX24.callMethod( "crm.lead.list", {
            filter: { ID: Array.from(idSet) },
            select: [ "*", "UF_*", "PHONE", "EMAIL" ]
         },
         response => {
            if (response.error()) {
               reject(response.error());
            } else {
               if (Array.isArray(response.data())) {
                  list = list.concat(response.data().map(item => new Lead(item)))
               }

               if (response.more()) {
                  response.next();
               } else {
                  resolve(list);
               }
            }
         }
      );
   })
};

const getUserList = idSet => {
   if (idSet.size === 0) return [];

   let list = [];
   return new Promise((resolve, reject) => {
      BX24.callMethod( "user.get",  { ID: Array.from(idSet) },
         response => {
            if (response.error()) {
               reject(response.error());
            } else {
               if (Array.isArray(response.data())) {
                  list = list.concat(response.data().map(item => new User(item)))
               }

               if (response.more()) {
                  response.next();
               } else {
                  resolve(list);
               }
            }
         }
      );
   })
};

function setDatesFilter(filter, field, values) {
   if (values) {
      if (values[0]) {
         filter[`>=${field}`] = values[0].toJSON()
      }
      if (values[1]) {
         filter[`<=${field}`] = values[1].toJSON()
      }
   }
   return filter
}

class BX24RestService {
   install(handler) {
      const batch = [
         ['event.bind', { event: 'OnAppInstall', handler: handler}],
         ['event.bind', { event: 'OnAppUpdate', handler: handler}],
         ['event.bind', { event: 'OnAppUninstall', handler: handler}]
      ];

      return new Promise((resolve, reject) => {
         BX24.callBatch(batch, response => {
            logger.debug(response);
            if (response.error) {
               reject({
                  error: {
                     message: 'install.error',
                     description: response.error
                  }
               });
            }
            BX24.installFinish();

            resolve();
         });
      });
   }

   async getActivities(filter) {
      const activities = await getActivities(filter);

      const userIds = new Set();
      const contactIds = new Set();
      const leadIds = new Set();
      const dealIds = new Set();

      activities.forEach(item => {
         userIds.add(item.responsibleId);
         if (item.contactId) {
            contactIds.add(item.contactId)
         } else if (item.leadId) {
            leadIds.add(item.leadId)
         } else if (item.dealId) {
            dealIds.add(item.dealId)
         }
      });

      const deals = await getDeals({ idSet: dealIds });

      deals.forEach(item => {
         if (item.contactId != null) {
            contactIds.add(item.contactId)
         } else if (item.leadId) {
            leadIds.add(item.leadId)
         }
      });

      const contactMap = keyBy((await getContactList(contactIds)), 'id');
      const leadMap = keyBy((await getLeadList(leadIds)), 'id');
      const userMap = keyBy((await getUserList(userIds)), 'id');

      deals.forEach(item => {
         item.contact = contactMap[item.contactId];
         item.lead = leadMap[item.leadId];
      });

      const dealMap = keyBy(deals, 'id');

      activities.forEach(item => {
         item.contact = contactMap[item.contactId];
         item.lead = leadMap[item.leadId];
         if (item.dealId != null) {
            const deal = dealMap[item.dealId];
            if (deal) {
               item.contact = deal.contact;
               item.lead = deal.lead;
            }
         }
         item.responsible = userMap[item.responsibleId];
      });

      return activities
   }

   async getDeals(filter) {
      const deals = await getDeals(filter);

      const leads = new Set();
      const contacts = new Set();

      deals.forEach(item => {
         if (item.contactId) {
            contacts.add(item.contactId)
         } else if (item.leadId) {
            leads.add(item.leadId)
         }
      });

      const contactMap = keyBy((await getContactList(contacts)), 'id');
      const leadMap = keyBy((await getLeadList(leads)), 'id');

      deals.forEach(item => {
         item.contact = contactMap[item.contactId];
         item.lead = leadMap[item.leadId];
      });

      // rest can't join, so do this shit
      return deals.filter(deal => {
         let match = true;

         if (filter.datesReg && filter.datesReg.length > 0) {
            if (filter.datesReg[0]) {
               match &= filter.datesReg[0] <= deal.contact?.regDate
            }
            if (filter.datesReg[1]) {
               match &= filter.datesReg[1] >= deal.contact?.regDate
            }
         }

         if (filter.resident && filter.resident.length > 0) {
            match &= filter.resident.includes(deal.contact?.crpId)
         }

         return match
      })
   }

   async findUsers(query) {
      query = query ? query.trim().replace(/\s+/g, ' ') : '';
      const batch = {
         name: ['user.search', { FIND: query }],
         email: ['user.search', { EMAIL: map(query.split(' '), term => `%${term}%`) }]
      };

      return new Promise((resolve, reject) => {
         BX24.callBatch(batch, response => {
            if (response.result_error && response.result_error.length > 0) {
               logger.error(response.result_error);
               reject();
            } else {
               const data = [];
               Object.keys(response).forEach(key => {
                  response[key].data().forEach(item => {
                     data.push({
                        value: item.ID,
                        label: [item.LAST_NAME, item.NAME, item.SECOND_NAME].join(' ').trim() || item.EMAIL
                     })
                  })
               });

               logger.debug(data, "Data");

               resolve(sortBy(data, ['name']));
            }
         })
      })
   }
}

export default new BX24RestService()